import { Link } from "react-router-dom";
import { PROJECT_GROUPS, PROJECTS, projectsInGroup, type Project } from "../lib/projects";

/** Catalog code for a project, in the house style: PR-0001, PR-0002, … */
function projectCode(project: Project): string {
  const index = PROJECTS.indexOf(project) + 1;
  return `PR-${String(index).padStart(4, "0")}`;
}

/** One project: code, name (linked out), pitch, and a short description. */
function ProjectCard({ project }: { project: Project }) {
  return (
    <article className="project-card" id={project.id}>
      <p className="accession accession--ink">{projectCode(project)}</p>

      <h3 className="project-card__title">
        {project.current ? (
          <Link to="/">{project.name}</Link>
        ) : (
          <a href={project.url} target="_blank" rel="noopener noreferrer">
            {project.name} ↗
          </a>
        )}
      </h3>

      <p className="project-card__tagline">{project.tagline}</p>
      <p className="project-card__blurb">{project.blurb}</p>

      <p className="project-card__foot">
        <span className="mono">{project.domain}</span>
        {project.current && <span className="project-flag">You are here</span>}
        {project.upcoming && (
          <span className="project-flag project-flag--soon">Not live yet</span>
        )}
      </p>
    </article>
  );
}

/**
 * Projects & Partners — the whole network in one place, grouped by family:
 * the law practice, the UpLaw tools, each standalone product filed under the
 * work it does, and the personal projects. Each entry gets a catalog code, like
 * everything else on this site.
 */
export function ProjectsPage() {
  return (
    <div className="page">
      <div className="container">
        <header className="page__head">
          <p className="eyebrow">Projects &amp; Partners</p>
          <h1 className="page__title">Projects &amp; Partners</h1>
          <p className="page__intro">
            Everything I build, in one index &mdash; a law practice, a stack of
            legal-technology tools, a few ventures, and the projects I make for
            no reason other than wanting them to exist.
          </p>
        </header>

        {PROJECT_GROUPS.map((group) => (
          <section className="section" key={group.id}>
            <div className="section__head">
              <div>
                <p className="eyebrow">{group.note}</p>
                <h2>{group.label}</h2>
              </div>
            </div>

            <div className="project-grid">
              {projectsInGroup(group.id).map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </section>
        ))}

        <p className="mono" style={{ fontSize: "0.85rem", marginTop: "var(--space-4)" }}>
          Want to work together, or curious about one of these? The{" "}
          <Link to="/contact">contact page</Link> is the way in.
        </p>
      </div>
    </div>
  );
}
